/**
 * Per-user Activity Timeline drawer.
 *  - Realtime logs for one user.
 *  - Derives sessions by pairing app_open → session_end and shows durations.
 *  - Date-range filter (client-side).
 *  - CSV export of the filtered rows (client blob download).
 */
import { useMemo, useState } from 'react';
import type { User } from '@mitang/shared';
import { useUserActivity } from '../hooks/useFirestore';
import { deriveSessions, type Session } from '../lib/analytics';
import {
  describeLog,
  EVENT_LABEL,
  modelNumberOf,
  productIdOf,
  categoryOf,
  queryOf,
} from '../lib/activityMeta';
import { toCsv, downloadCsv } from '../lib/csv';
import { formatDateTime, formatDuration, timeAgo } from '../lib/format';
import { Drawer } from './Drawer';
import { Button, Badge } from './ui';
import { EmptyState, ErrorState, LoadingState } from './States';
import type { ActivityLogWithId } from '../lib/collections';
import { useProductsByIds } from '../hooks/useProductsByIds';

function toEpoch(dateStr: string, endOfDay = false): number | null {
  if (!dateStr) return null;
  const d = new Date(`${dateStr}T${endOfDay ? '23:59:59.999' : '00:00:00.000'}`);
  const t = d.getTime();
  return Number.isNaN(t) ? null : t;
}

export function ActivityTimeline({
  user,
  open,
  onClose,
}: {
  user: User | null;
  open: boolean;
  onClose: () => void;
}): JSX.Element | null {
  const activity = useUserActivity(open ? (user?.id ?? null) : null);
  // This dealer's saved products. The ids are already on the user doc; only the
  // products they actually saved are fetched, so this is a handful of reads.
  const savedIds = useMemo(() => (open ? (user?.wishlist ?? []) : []), [open, user?.wishlist]);
  const savedProducts = useProductsByIds(savedIds);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const fromTs = toEpoch(from, false);
  const toTs = toEpoch(to, true);

  const filtered = useMemo(() => {
    return activity.data.filter((l) => {
      if (fromTs !== null && l.ts < fromTs) return false;
      if (toTs !== null && l.ts > toTs) return false;
      return true;
    });
  }, [activity.data, fromTs, toTs]);

  const sessions = useMemo(() => deriveSessions(filtered), [filtered]);

  const summary = useMemo(() => summarize(filtered, sessions), [filtered, sessions]);

  const onExport = () => {
    if (!user) return;
    const rows = filtered.map((l) => ({
      timestamp: formatDateTime(l.ts),
      epochMs: l.ts,
      event: EVENT_LABEL[l.event],
      modelNumber: modelNumberOf(l) ?? '',
      productId: productIdOf(l) ?? '',
      category: categoryOf(l) ?? '',
      query: queryOf(l) ?? '',
    }));
    // Model no leads the product columns because it is the one people read; the
    // internal id stays for joining back to the catalogue. Category is filled
    // for product rows too now (it used to be blank unless the event was a
    // category view), so a pivot by sheet actually works.
    const csv = toCsv(rows, [
      { key: 'timestamp', header: 'Timestamp' },
      { key: 'epochMs', header: 'Epoch (ms)' },
      { key: 'event', header: 'Event' },
      { key: 'modelNumber', header: 'Model no' },
      { key: 'category', header: 'Category' },
      { key: 'productId', header: 'Product ID' },
      { key: 'query', header: 'Search query' },
    ]);
    const safeName = user.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase();
    downloadCsv(`activity-${safeName}-${user.id}.csv`, csv);
  };

  if (!user) return null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={user.name}
      subtitle={`${user.companyName} · ${user.mobile}`}
      footer={
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-500">{filtered.length} events in view</span>
          <Button variant="secondary" size="sm" onClick={onExport} disabled={filtered.length === 0}>
            Export CSV
          </Button>
        </div>
      }
    >
      {savedIds.length > 0 ? (
        <section className="mb-4 rounded-lg border border-slate-200 bg-white">
          <header className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
            <h3 className="text-xs font-semibold text-slate-900">
              Saved products
              <span className="ml-1.5 font-normal text-slate-500">({savedIds.length})</span>
            </h3>
            <span className="text-[11px] text-slate-400">what this dealer intends to buy</span>
          </header>
          <ul className="divide-y divide-slate-50">
            {savedIds.map((id) => (
              <li key={id} className="flex items-baseline gap-2 px-3 py-1.5 text-xs">
                <span className="min-w-0 truncate font-medium text-slate-800">
                  {savedProducts.get(id)?.modelNumber ?? id}
                </span>
                <span className="truncate text-slate-500">
                  {savedProducts.get(id)?.category ?? ''}
                </span>
                <span className="ml-auto shrink-0 text-slate-400">
                  {user.wishlistAt?.[id] ? timeAgo(user.wishlistAt[id]) : ''}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* Date filter */}
      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
        <label className="text-xs font-medium text-slate-600">
          From
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="mt-1 block rounded-md border border-slate-300 px-2 py-1 text-sm"
          />
        </label>
        <label className="text-xs font-medium text-slate-600">
          To
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="mt-1 block rounded-md border border-slate-300 px-2 py-1 text-sm"
          />
        </label>
        {(from || to) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setFrom('');
              setTo('');
            }}
          >
            Clear
          </Button>
        )}
      </div>

      {/* Summary chips */}
      <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <SummaryChip label="Sessions" value={summary.sessionCount} />
        <SummaryChip label="Total time" value={formatDuration(summary.totalMs)} />
        <SummaryChip label="Products viewed" value={summary.productViews} />
        <SummaryChip label="Searches" value={summary.searches} />
        <SummaryChip label="Shares" value={summary.shares} />
        <SummaryChip label="Avg session" value={formatDuration(summary.avgMs)} />
      </div>

      {activity.loading ? (
        <LoadingState label="Loading timeline…" />
      ) : activity.error ? (
        <ErrorState message={activity.error} />
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No activity in this range"
          hint="Adjust the date filter or check back after the user opens the app."
        />
      ) : (
        <ol className="relative space-y-4 border-l border-slate-200 pl-5">
          {filtered.map((log) => (
            <TimelineItem key={log.id} log={log} />
          ))}
        </ol>
      )}
    </Drawer>
  );
}

function summarize(logs: ActivityLogWithId[], sessions: Session[]) {
  const totalMs = sessions.reduce((sum, s) => sum + s.durationMs, 0);
  return {
    sessionCount: sessions.length,
    totalMs,
    avgMs: sessions.length ? Math.round(totalMs / sessions.length) : 0,
    productViews: logs.filter((l) => l.event === 'viewed_product').length,
    searches: logs.filter((l) => l.event === 'searched').length,
    shares: logs.filter((l) => l.event === 'shared_product').length,
  };
}

function SummaryChip({ label, value }: { label: string; value: string | number }): JSX.Element {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
      <p className="text-[11px] uppercase tracking-wide text-slate-400">{label}</p>
      <p className="text-sm font-semibold text-slate-800">{value}</p>
    </div>
  );
}

const eventTone: Record<string, 'brand' | 'success' | 'warn' | 'neutral' | 'danger'> = {
  app_open: 'brand',
  session_end: 'neutral',
  viewed_product: 'success',
  viewed_category: 'success',
  searched: 'warn',
  shared_product: 'danger',
  filter_used: 'neutral',
};

function TimelineItem({ log }: { log: ActivityLogWithId }): JSX.Element {
  return (
    <li className="relative">
      <span
        aria-hidden
        className="absolute -left-[1.4rem] top-1 h-2.5 w-2.5 rounded-full border-2 border-white bg-brand-400 ring-1 ring-slate-200"
      />
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={eventTone[log.event] ?? 'neutral'}>{EVENT_LABEL[log.event]}</Badge>
        <span className="text-xs text-slate-400" title={formatDateTime(log.ts)}>
          {timeAgo(log.ts)}
        </span>
      </div>
      <p className="mt-1 text-sm text-slate-700">{describeLog(log)}</p>
      <p className="text-[11px] text-slate-400">{formatDateTime(log.ts)}</p>
    </li>
  );
}
